// utils/time.ts
import { addDays, isAfter, isBefore, parse, format, isValid } from 'date-fns';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { OfficeHoursItem } from 'components/authorable/shared/content/LocationHeader';

export function getCentralTimeNow(format = 'yyyy-MM-dd HH:mm:ss zzz') {
  const now = new Date();
  // Returns a string like "2025-08-14 09:32:10 CDT" or "CST" in winter
  return formatInTimeZone(now, 'America/Chicago', format);
}

export function getCentralIsoNow() {
  const now = new Date();
  // ISO-like string in Central with offset, e.g. "2025-08-14T09:32:10-05:00"
  return formatInTimeZone(now, 'America/Chicago', "yyyy-MM-dd'T'HH:mm:ssXXX");
}

export const convert24hToAmPm = (time24: string | undefined): string => {
  if (!time24) {
    return '';
  }

  // Try parsing HH:mm:ss first, then HH:mm
  const date = parse(time24, 'HH:mm', new Date());

  if (!isValid(date)) {
    return '';
  }

  return format(date, 'h:mm a').replace('AM', 'a.m.').replace('PM', 'p.m.');
};
export const locationOpenTwentyFourHoursMessage = 'Open 24 Hours';

/**
 * Converts a date field from Coveo (Unix epoch milliseconds(UTC)) to human-readable time in Central Time (DST Aware)
 * @param {number} epochMs
 * @returns {string} centralString
 */
export function epochToCentralTime(epochMs: number): string {
  if (epochMs == null || isNaN(epochMs)) {
    return '';
  }
  const utcDate = new Date(epochMs);

  // Extract Central Time components (DST-aware)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(utcDate);

  const get = (type: string) => parts.find((p) => p.type === type)?.value;

  const year = get('year');
  const month = get('month');
  const day = get('day');
  const hour = get('hour');
  const minute = get('minute');

  return `${year}-${month}-${day}@${hour}:${minute}`;
}

/**
 * Build ISO 8601 with zone offset from a date (any parseable instant) + HH:mm (Chicago local time).
 * @param dateOrInstant string | number | Date — e.g. "2025-12-19T12:00:00Z" or a Date object
 * @param time24h 'HH:mm' — e.g. '17:30'
 * @param tz IANA zone — default 'America/Chicago'
 * @returns e.g. "2025-12-19T17:30:00-06:00" (or -05:00 in summer)
 */
export function setLocalTimeOnDateWithOffset(
  dateOrInstant: string | number | Date,
  time24h: string,
  tz: string = 'America/Chicago'
): string {
  const dateObj = new Date(dateOrInstant);

  if (!isValid(dateObj)) {
    throw new Error(`Invalid dateOrInstant: ${String(dateOrInstant)}`);
  }

  if (!/^\d{2}:\d{2}$/.test(time24h)) {
    throw new Error(`time24h must be 'HH:mm', got "${String(time24h)}"`);
  }

  const dateStr = formatInTimeZone(dateObj, tz, 'yyyy-MM-dd');

  // Add the time and create a new date object
  const combinedZonedDate = fromZonedTime(`${dateStr} ${time24h}`, tz);

  // Return the formatted string with the CORRECT offset for that specific time
  return formatInTimeZone(combinedZonedDate, tz, `yyyy-MM-dd'T'HH:mm:ssXXX`);
}

/**
 * Handle now open, next open date
 */
export function getNextOpenDateTime(
  locationStatus: LocationStatus,
  nextOpenInDays: number,
  nextOpenTime: string
): string {
  const formattedTime = format(parse(locationStatus?.time, 'h:mm a', new Date()), 'HH:mm');
  const targetDate = locationStatus?.isOpen
    ? new Date()
    : locationStatus?.message.includes('Opens') //implies closed now but open tomorrow or later
      ? addDays(new Date(), nextOpenInDays) // add days to get the next open day
      : new Date(); //fall thru is today
  nextOpenTime = setLocalTimeOnDateWithOffset(targetDate, formattedTime, 'America/Chicago');

  return nextOpenTime;
}

/**
 * Normalize time format to HH:mm
 * Handles cases where time might be "17" instead of "17:00"
 */
export const normalizeTimeFormat = (time: string): string => {
  if (!time) return '';

  // If time is just hour (e.g., "17"), add ":00"
  if (time.length <= 2 && !time.includes(':')) {
    return `${time.padStart(2, '0')}:00`;
  }

  // If time is already in HH:mm format, return as is
  if (time.includes(':')) {
    const [hour, minute] = time.split(':');
    return `${hour.padStart(2, '0')}:${minute || '00'}`;
  }

  return time;
};

/**
 * Get current day name in specified timezone
 * @param timezone - Timezone to use (defaults to America/Chicago for CST/CDT)
 * @returns Current day as string
 */
export function getCurrentDay(timezone: string = 'America/Chicago'): string | number {
  const now = new Date();
  return formatInTimeZone(now, timezone, 'EEEE'); // "Monday"
}

/**
 * Find the next available office hours day from the office hours array
 * @param officeHours - Array of office hours items
 * @param currentDay - Current day name (e.g., "Tuesday")
 * @returns Next office hours date
 */
export function getNextOfficeHoursDay(
  officeHours: OfficeHoursItem[],
  currentDay: string
): { Date: Date; openTime: string; nextOpenInDays: string } {
  const now = new Date();

  // Map full day names to abbreviations used in office hours
  const dayMap: { [key: string]: string[] } = {
    Monday: ['Mon', 'Monday'],
    Tuesday: ['Tues', 'Tue', 'Tuesday'],
    Wednesday: ['Wed', 'Weds', 'Wednesday'],
    Thursday: ['Thu', 'Thurs', 'Thursday'],
    Friday: ['Fri', 'Friday'],
    Saturday: ['Sat', 'Saturday'],
    Sunday: ['Sun', 'Sunday'],
  };

  // Week order for finding next day
  const weekOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Get available office hours days in full day name format
  const availableDays = officeHours
    ?.map((oh) => {
      const dayAbbr = oh?.fields?.officeHourType?.fields?.dayofWeekAbbr?.value;
      if (!dayAbbr) return null;

      // Find the full day name that matches this abbreviation
      for (const [fullDay, abbrs] of Object.entries(dayMap)) {
        if (abbrs.includes(dayAbbr)) {
          return fullDay;
        }
      }
      return null;
    })
    .filter((day) => day !== null) as string[];

  if (availableDays.length === 0) {
    // No office hours found, default to next day
    return { Date: addDays(now, 1), openTime: '', nextOpenInDays: '0' };
  }

  // Find the current day index
  const currentDayIndex = weekOrder.indexOf(currentDay);

  // Look for the next available office hours day
  let updatedAvailableDays = availableDays;
  let isDaysUpdated = false;

  do {
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDayName = weekOrder[nextDayIndex];

      if (availableDays.includes(nextDayName)) {
        const openTime = convert24hToAmPm(
          officeHours?.find(
            (oh) => oh.fields?.officeHourType?.fields?.dayofWeek?.value === nextDayName
          )?.fields?.open?.value ?? ''
        );

        if (openTime === '') {
          updatedAvailableDays = updatedAvailableDays.filter((day) => day !== nextDayName);
          isDaysUpdated = true;
        } else {
          isDaysUpdated = false;
          const nextOpenInDays = i.toString();
          const openTime =
            officeHours?.find(
              (oh) => oh.fields?.officeHourType?.fields?.dayofWeek?.value === nextDayName
            )?.fields?.open?.value ?? '';
          return {
            Date: addDays(now, i),
            openTime: openTime,
            nextOpenInDays,
          };
        }
      }
    }
  } while (isDaysUpdated);

  // Fallback: if no office hours found, return next day
  return {
    Date: addDays(now, 1),
    openTime: '',
    nextOpenInDays: '0',
  };
}

export interface LocationStatus {
  isOpen: boolean;
  time: string;
  message: string;
  iconColor: 'green' | 'red';
  iconType: 'clock-bold';
  nextOpenInDays: string;
  nextOpenTime?: string;
}

/**
 * Determines location status based on open and close times
 * @param openTime - Opening time in format "HH:mm" (24-hour format, e.g., "09:00")
 * @param closeTime - Closing time in format "HH:mm" (24-hour format, e.g., "17:00")
 * @param timezone - Timezone to use (defaults to America/Chicago for CST/CDT)
 * @returns LocationStatus object with message and icon state
 */
export function getLocationStatus(
  openTime: string,
  closeTime: string,
  officeHours: OfficeHoursItem[],
  timezone: string = 'America/Chicago'
): LocationStatus | null {
  // Get current time in the specified timezone
  const now = new Date();
  const currentTimeStr = formatInTimeZone(now, timezone, 'HH:mm');
  const currentDate = formatInTimeZone(now, timezone, 'yyyy-MM-dd');

  // Normalize time formats (handles "17" -> "17:00")
  const normalizedOpenTime = normalizeTimeFormat(openTime);
  const normalizedCloseTime = normalizeTimeFormat(closeTime);

  // Parse times for today
  const todayOpen = parse(`${currentDate} ${normalizedOpenTime}`, 'yyyy-MM-dd HH:mm', new Date());
  const todayClose = parse(`${currentDate} ${normalizedCloseTime}`, 'yyyy-MM-dd HH:mm', new Date());
  const currentDateTime = parse(`${currentDate} ${currentTimeStr}`, 'yyyy-MM-dd HH:mm', new Date());

  // Check if 24 hours open
  if (normalizedOpenTime === normalizedCloseTime && normalizedOpenTime === '00:00') {
    return {
      isOpen: true,
      message: locationOpenTwentyFourHoursMessage,
      time: normalizedOpenTime,
      iconColor: 'green',
      iconType: 'clock-bold',
      nextOpenInDays: '0',
      nextOpenTime: normalizedOpenTime,
    };
  }

  // Check if currently open
  if (isAfter(currentDateTime, todayOpen) && isBefore(currentDateTime, todayClose)) {
    // Location is open - show close time
    const closeTimeFormatted = convert24hToAmPm(normalizedCloseTime);
    return {
      isOpen: true,
      message: `Open until `,
      time: closeTimeFormatted,
      iconColor: 'green',
      iconType: 'clock-bold',
      nextOpenInDays: '0',
      nextOpenTime: normalizedOpenTime,
    };
  }

  // Location is closed
  if (isBefore(currentDateTime, todayOpen)) {
    // Closed but opens later today
    const openTimeFormatted = convert24hToAmPm(normalizedOpenTime);
    return {
      isOpen: false,
      message: `Opens at `,
      time: openTimeFormatted,
      iconColor: 'red',
      iconType: 'clock-bold',
      nextOpenInDays: '0',
      nextOpenTime: normalizedOpenTime,
    };
  }

  // Closed for the day - find next opening day
  const currentDayName = formatInTimeZone(now, timezone, 'EEEE'); // Current day name
  const {
    Date: nextOfficeDay,
    openTime: nextOpenTime,
    nextOpenInDays,
  } = getNextOfficeHoursDay(officeHours, currentDayName);
  // check nextOfficeDay - is it a full date?
  const dayName = formatInTimeZone(nextOfficeDay, timezone, 'EEEE'); // Full day name
  const openTimeFormatted = convert24hToAmPm(nextOpenTime);

  if (openTimeFormatted === '') {
    return null;
  }
  return {
    isOpen: false,
    message: `Opens ${dayName} at `,
    time: openTimeFormatted,
    iconColor: 'red',
    iconType: 'clock-bold',
    nextOpenInDays: nextOpenInDays,
    nextOpenTime: normalizedOpenTime,
  };
}
